import { type DrawerProps, Form, Result } from 'antd';
import { useField } from 'formik';
import { isEmpty, keyBy, pick, uniqBy } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type BankOption, useListBankOptionsQuery, type VariantBankCodeMappingInput } from '../../api';
import { useCompany } from '../contexts/CompanyContextManager';
import DrawerWithAutoWidth from '../DrawerWithAutoWidth';
import InputField from './InputField';

export type VariantBankCodeMappingDrawerProps = Pick<DrawerProps, 'open' | 'onClose'> & {
    name: string;
};

const useBankData = (name: string, company?: { id: string }) => {
    const [{ value }, , { setValue }] = useField<VariantBankCodeMappingInput[]>({ name });
    const { data } = useListBankOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { companyId: company?.id, allowVariantCodeMapping: true } },
    });

    const bankMap = useMemo(() => keyBy('id', data?.list || []), [data?.list]);

    useEffect(() => {
        if (data?.list) {
            const emptyBankFields = data.list.map(bank => ({ bankId: bank.id, code: '' }));
            const parsedValue = (value || []).map(bank => pick(['bankId', 'code'], bank));
            const merged = uniqBy('bankId', [...parsedValue, ...emptyBankFields]).filter(bank => bankMap[bank.bankId]);
            setValue(merged);
        }
    }, [data, setValue]);

    return bankMap;
};

const VariantBankCodeMappingDrawer = ({ onClose, open, name }: VariantBankCodeMappingDrawerProps) => {
    const { t } = useTranslation('variant');
    const company = useCompany(true);
    const bankMap = useBankData(name, company);
    const [{ value }] = useField<{ bankId: string; code: string }[]>({ name });

    if (isEmpty(bankMap)) {
        return (
            <DrawerWithAutoWidth onClose={onClose} open={open} title={t('variant:bankCodeMapping.title')}>
                <Result status="info" subTitle={t('variant:bankCodeMapping.message.noBank')} />
            </DrawerWithAutoWidth>
        );
    }

    return (
        <DrawerWithAutoWidth onClose={onClose} open={open} title={t('variant:bankCodeMapping.title')}>
            <Form layout="vertical">
                {value.map((bank, index) => (
                    <InputFieldGroup key={bank.bankId} bank={bank} bankMap={bankMap} index={index} name={name} />
                ))}
            </Form>
        </DrawerWithAutoWidth>
    );
};

const InputFieldGroup = ({
    bank,
    name,
    index,
    bankMap,
}: {
    bank: VariantBankCodeMappingInput;
    name: string;
    index: number;
    bankMap: Record<string, BankOption>;
}) => (
    <>
        <InputField
            itemProps={{ hidden: true }}
            label={bankMap[bank.bankId]?.displayName}
            name={`${name}[${index}].bankId`}
        />
        <InputField label={bankMap[bank.bankId]?.displayName} name={`${name}[${index}].code`} />
    </>
);

export default VariantBankCodeMappingDrawer;

export const useVariantBankCodeMappingDrawer = () => {
    const [open, setOpen] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setOpen(true),
            close: () => setOpen(false),
        }),
        []
    );

    return {
        ...actions,
        render: (props: VariantBankCodeMappingDrawerProps) => (
            <VariantBankCodeMappingDrawer {...props} onClose={actions.close} open={open} />
        ),
    };
};
